import { type ReactNode } from 'react';
import { LockIcon } from 'lucide-react';
import clsx from 'clsx';
import type { ProjectListItem } from '@/entities/project';
import { DropdownMenuItem } from '@monksflow/monks-ui';
import { PermissionTooltip } from '@/shared/ui';
import { usePermissionCheck } from '@/shared/hooks/usePermissionCheck';

interface ProjectPickerItemProps {
  project: ProjectListItem;
  onClick: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  trailing?: ReactNode;
  role?: 'button' | 'checkbox';
  ariaChecked?: boolean;
  /** Prevent menu from closing when item is selected (useful for multi-select) */
  preventClose?: boolean;
  /**
   * Any-of the project-scoped permissions needed to pick this project. An
   * unpublished project, or one whose permissions were never requested, counts as
   * denied — so forgetting `includePermissions` on the popover fails closed.
   * Required so a consumer cannot render an ungated row by omission.
   */
  requiredPermissions: readonly string[];
  deniedMessage?: string;
}

export function ProjectPickerItem({
  project,
  onClick,
  onKeyDown,
  trailing,
  role = 'button',
  ariaChecked,
  preventClose = false,
  requiredPermissions,
  deniedMessage,
}: ProjectPickerItemProps) {
  const check = usePermissionCheck();
  const denied = !(!!project.platformProjectId && check(project.permissions, requiredPermissions));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
    onKeyDown?.(e);
  };

  return (
    <PermissionTooltip disabled={denied} message={deniedMessage}>
      <DropdownMenuItem
        disabled={denied}
        onClick={denied ? undefined : onClick}
        onKeyDown={denied ? undefined : handleKeyDown}
        onSelect={preventClose ? (event) => event.preventDefault() : undefined}
        role={role}
        aria-checked={ariaChecked}
        tabIndex={denied ? undefined : 0}
        className={clsx('flex items-center gap-2 justify-between', !denied && 'cursor-pointer')}
      >
        {project.title}
        <span className="flex items-center gap-1.5">
          {denied && (
            <LockIcon
              data-testid="project-picker-lock"
              className="size-3.5 shrink-0 text-muted-foreground"
            />
          )}
          {trailing}
        </span>
      </DropdownMenuItem>
    </PermissionTooltip>
  );
}
